// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// render.ts — everything you can see on the board, painted on ONE canvas under the DOM hit targets.
//
// Why a canvas at all when the grid is DOM: because a DOM background is not a composite. SKILL.md's
// contrast probe reads getImageData at a cell's centre and compares it to an empty cell — against
// transparent DOM divs that reads rgba(0,0,0,0) and proves nothing. Painting the fills here means
// the probe measures what a player actually sees, while the DOM above keeps native hit-testing (and
// with it every stale-rect and coord-scaling bug this factory has already paid for twice).
//
// NO COLOUR LITERALS. Every colour comes from palette.ts, which is where they can be measured.
//
// FIRE AND WATER ARE TOLD APART BY SHAPE, NOT BRIGHTNESS: water rises from the bottom behind a flat
// waterline, fire falls from the top as flickering triangles clipped ABOVE that waterline. That
// clipping is not a detail — it is the render rule that makes `fire over floodFill` unreachable, and
// tests/contrast.test.ts names it in NEVER_COMPOSITED.

import { FLOOD_ALPHA, PALETTE } from './palette';
import type { Game } from './game';
import type { Mode } from './modes';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  col: string;
  r: number;
}

export interface View {
  /** Board geometry in CSS px, relative to the canvas. */
  rects: Rect[];
  selected: number;
  /** 0..1 progress of the commit surge, or -1 when idle. */
  surge: number;
  /** Live preview path while a finger is down, or null. */
  preview: number[] | null;
  shake: number;
  reduced: boolean;
}

const rr = (c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  const k = Math.min(r, w / 2, h / 2);
  c.beginPath();
  c.moveTo(x + k, y);
  c.arcTo(x + w, y, x + w, y + h, k);
  c.arcTo(x + w, y + h, x, y + h, k);
  c.arcTo(x, y + h, x, y, k);
  c.arcTo(x, y, x + w, y, k);
  c.closePath();
};

export function createRenderer(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  const parts: Particle[] = [];
  let t = 0;

  function resize(w: number, h: number): void {
    // Guard the transient 0-size measurement. A scale computed from a 0x0 rect yields NaN world
    // coordinates and silently drops every tap; ignore it and take the next frame.
    if (w <= 0 || h <= 0) return;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function burst(x: number, y: number, col: string, n: number, speed = 90, reduced = false): void {
    const count = reduced ? Math.max(2, Math.round(n / 4)) : n;
    for (let i = 0; i < count; i++) {
      const a = (Math.PI * 2 * i) / count + i * 0.7;
      const s = speed * (0.4 + ((i * 37) % 100) / 140);
      parts.push({
        x,
        y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s - 20,
        life: 0.5,
        max: 0.5,
        col,
        r: 2 + (i % 3),
      });
    }
    if (parts.length > 240) parts.splice(0, parts.length - 240);
  }

  function step(dt: number): void {
    t += dt;
    for (let i = parts.length - 1; i >= 0; i--) {
      const p = parts[i];
      p.life -= dt;
      if (p.life <= 0) {
        parts.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 220 * dt;
    }
  }

  function draw(g: Game, v: View): void {
    if (!ctx) return;
    const m: Mode = g.mode;
    const W = canvas.width / (Math.min(3, window.devicePixelRatio || 1));
    const H = canvas.height / (Math.min(3, window.devicePixelRatio || 1));
    ctx.clearRect(0, 0, W, H);

    ctx.save();
    if (v.shake > 0.001 && !v.reduced) {
      const s = v.shake;
      ctx.translate(Math.sin(t * 61) * s, Math.cos(t * 47) * s);
    }

    const powered = new Set(g.spine.slice(0, g.poweredLen));

    // ── compartments ────────────────────────────────────────────────────────
    for (let i = 0; i < g.n; i++) {
      const r = v.rects[i];
      if (!r) continue;
      const c = g.cells[i];
      const rad = Math.min(7, r.w * 0.09);

      // base surface
      ctx.fillStyle = i === v.selected ? PALETTE.sel : PALETTE.cell;
      rr(ctx, r.x, r.y, r.w, r.h, rad);
      ctx.fill();

      // water — rises from the BOTTOM, flat waterline. Clipped to the compartment so the fill is a
      // real composite the probe can read.
      if (c.flood > 0) {
        const fh = (r.h * Math.min(100, c.flood)) / 100;
        ctx.save();
        rr(ctx, r.x, r.y, r.w, r.h, rad);
        ctx.clip();
        // The alpha comes from palette.ts so the number the contrast test measures is the number
        // that gets painted. At 0.5 the water measured 2.42:1 against a dry compartment on the
        // deployed page — invisible by the standard, while every palette assertion stayed green.
        ctx.globalAlpha = FLOOD_ALPHA;
        ctx.fillStyle = PALETTE.flood;
        ctx.fillRect(r.x, r.y + r.h - fh, r.w, fh);
        ctx.globalAlpha = 1;
        ctx.fillStyle = PALETTE.flood;
        ctx.fillRect(r.x, r.y + r.h - fh, r.w, 2);
        ctx.restore();
      }

      // fire — falls from the TOP as triangles, clipped ABOVE the waterline (the render rule that
      // makes fire-over-water unreachable) and stroked against the hull so it reads at any depth.
      if (c.fire > 0) {
        const waterTop = r.y + r.h - (r.h * Math.min(100, c.flood)) / 100;
        ctx.save();
        rr(ctx, r.x, r.y, r.w, r.h, rad);
        ctx.clip();
        ctx.beginPath();
        ctx.rect(r.x, r.y, r.w, Math.max(0, waterTop - r.y));
        ctx.clip();
        const fh = (r.h * 0.62 * Math.min(100, c.fire)) / 100;
        const n = 4;
        ctx.fillStyle = PALETTE.fire;
        ctx.strokeStyle = PALETTE.hull;
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let k = 0; k < n; k++) {
          const x0 = r.x + (r.w * k) / n;
          const w = r.w / n;
          const flick = v.reduced ? 0 : Math.sin(t * 9 + k * 2.1 + i) * fh * 0.16;
          ctx.moveTo(x0, r.y);
          ctx.lineTo(x0 + w / 2, r.y + fh + flick);
          ctx.lineTo(x0 + w, r.y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }

      // a breach is a NOTCH in the border, drawn on the hull — never inside the water
      if (c.breaches > 0) {
        ctx.strokeStyle = PALETTE.breach;
        ctx.lineWidth = 3;
        for (let b = 0; b < c.breaches; b++) {
          const y = r.y + 8 + b * 9;
          ctx.beginPath();
          ctx.moveTo(r.x + 1, y);
          ctx.lineTo(r.x + 8, y + 4);
          ctx.lineTo(r.x + 1, y + 8);
          ctx.stroke();
        }
      }

      // a scarred bulkhead: a permanent corner mark, so "this costs two now" is visible
      if (c.warp && !c.destroyed) {
        ctx.strokeStyle = PALETTE.dead;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(r.x + r.w - 12, r.y + r.h - 3);
        ctx.lineTo(r.x + r.w - 3, r.y + r.h - 12);
        ctx.stroke();
      }

      // destroyed: an X hatch, readable wet as well as dry
      if (c.destroyed) {
        ctx.strokeStyle = PALETTE.dead;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(r.x + 7, r.y + 7);
        ctx.lineTo(r.x + r.w - 7, r.y + r.h - 7);
        ctx.moveTo(r.x + r.w - 7, r.y + 7);
        ctx.lineTo(r.x + 7, r.y + r.h - 7);
        ctx.stroke();
      }

      // a charged battery cell: this compartment is free while it lasts
      if (c.charge > 0) {
        ctx.fillStyle = PALETTE.good;
        const bw = Math.max(10, r.w * 0.22);
        ctx.fillRect(r.x + 5, r.y + 5, bw * (c.charge / 12), 3);
        ctx.strokeStyle = PALETTE.good;
        ctx.lineWidth = 1;
        ctx.strokeRect(r.x + 5, r.y + 5, bw, 3);
      }
    }

    // ── the cable ───────────────────────────────────────────────────────────
    const route = v.preview ?? g.spine;
    const pts = route.map((i) => {
      const r = v.rects[i];
      return r ? { x: r.x + r.w / 2, y: r.y + r.h / 2 } : null;
    });
    const litLen = v.preview ? previewPowered(g, v.preview) : g.poweredLen;

    // the tail first, so the powered run draws over it
    if (route.length > litLen) {
      ctx.save();
      ctx.setLineDash([7, 6]);
      ctx.lineWidth = 7;
      ctx.strokeStyle = PALETTE.hull; // the halo that keeps the tail readable inside the water
      strokePath(ctx, pts, Math.max(0, litLen - 1), route.length - 1);
      ctx.lineWidth = 4;
      ctx.strokeStyle = PALETTE.dark;
      strokePath(ctx, pts, Math.max(0, litLen - 1), route.length - 1);
      ctx.restore();
    }

    if (litLen > 1) {
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 8;
      ctx.strokeStyle = PALETTE.hull;
      strokePath(ctx, pts, 0, litLen - 1);
      ctx.lineWidth = 5;
      ctx.strokeStyle = PALETTE.wire;
      strokePath(ctx, pts, 0, litLen - 1);
      ctx.restore();
    }

    // the boundary marker: the last compartment that actually has power
    if (litLen > 0 && litLen < route.length) {
      const p = pts[litLen - 1];
      if (p) {
        ctx.strokeStyle = PALETTE.wire;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 11, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    // the surge: a bright head running the cable outward on commit
    if (v.surge >= 0 && litLen > 1) {
      const head = v.surge * (litLen - 1);
      const k = Math.min(litLen - 1, Math.floor(head));
      const a = pts[k];
      const b = pts[Math.min(litLen - 1, k + 1)];
      if (a && b) {
        const f = head - k;
        ctx.fillStyle = PALETTE.text;
        ctx.beginPath();
        ctx.arc(a.x + (b.x - a.x) * f, a.y + (b.y - a.y) * f, 7, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ── labels, LAST ────────────────────────────────────────────────────────
    // Drawn after the cable, not with the compartments. The cable runs through the centre of every
    // cell it powers, so painted first the labels came out as "HE|M" and "COO|ANT" with the amber
    // straight through them — the compartments you can least afford to misread are exactly the ones
    // the cable is on.
    ctx.font = `600 ${Math.max(9, Math.min(12, (v.rects[0]?.w ?? 100) * 0.105))}px ui-monospace, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    for (let i = 0; i < g.n; i++) {
      const r = v.rects[i];
      const kind = m.kind[i];
      if (!r || kind === 'conduit') continue;
      const label = kind === 'reactor' ? 'REACTOR' : kind.toUpperCase();
      const x = r.x + r.w / 2;
      const y = r.y + r.h - 5;
      ctx.lineWidth = 3;
      ctx.strokeStyle = PALETTE.hull;
      ctx.strokeText(label, x, y);
      ctx.fillStyle = powered.has(i) ? PALETTE.text : PALETTE.dim;
      ctx.fillText(label, x, y);
    }

    // ── particles ───────────────────────────────────────────────────────────
    for (const p of parts) {
      ctx.globalAlpha = Math.max(0, p.life / p.max);
      ctx.fillStyle = p.col;
      ctx.fillRect(p.x - p.r / 2, p.y - p.r / 2, p.r, p.r);
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  return { resize, draw, step, burst, particles: parts };
}

function strokePath(
  ctx: CanvasRenderingContext2D,
  pts: ({ x: number; y: number } | null)[],
  from: number,
  to: number,
): void {
  ctx.beginPath();
  let started = false;
  for (let i = from; i <= to; i++) {
    const p = pts[i];
    if (!p) continue;
    if (!started) {
      ctx.moveTo(p.x, p.y);
      started = true;
    } else ctx.lineTo(p.x, p.y);
  }
  if (started) ctx.stroke();
}

/**
 * How much of a PREVIEW route the reactor could afford, using the shipped cost and conduction rules.
 *
 * This is the number that greys the cable out live under the player's finger, so it has to agree
 * with what will happen on release — it reads `g.costOf` and `g.conducts` rather than re-deriving
 * them, because a preview that disagrees with the commit is a lie told at exactly the moment the
 * player is deciding.
 */
export function previewPowered(g: Game, route: readonly number[]): number {
  let spent = 0;
  let len = 0;
  for (const i of route) {
    if (!g.conducts(i)) break;
    const c = spent + g.costOf(i);
    if (c > g.output) break;
    spent = c;
    len++;
  }
  return len;
}
